import React, { useState } from 'react'
import { useTransition } from 'react-spring'
import { CarouselCard, CarouselIconButton, DotsWrapper } from './CarouselComponent.css'
import ArrowBackIcon from 'assets/icons/prev-carousel-arrow.svg'
import ArrowForwardIcon from 'assets/icons/next-carousel-arrow.svg'
import { useToggleState } from 'lib/hooks/useToggleState'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import styled from 'styled-components'
import theme from 'stories/utils/theme'
import { ButtonText, Headline4, QuoteText, Subtitle1 } from 'stories/elements/Typography/Text'
import DynamicButton from 'components/modules/LandingPage/DynamicButton'
import NextImage from 'components/elements/NextImage'

interface CarouselDotProps {
    isClicked: boolean
}
interface CarouselProps {
    buttonLink: string
    buttonText: string
    carouselData: {
        text: string
        image: string
        footerInfo?: {
            title: string
            subtitle: string
        }
    }[]
}

const Component = styled.div`
    display: flex;
    flex-direction: column;
    gap: 50px;
    align-items: center;
    @media (max-width: 750px) {
        gap: 30px;
    }
`

const Container = styled.div`
    display: flex;
    width: 100%;
    align-self: center;
    justify-self: center;
    height: 450px;
    @media (max-width: 750px) {
        height: 600px;
        button {
            display: none;
        }
    }
`

const Wrapper = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: flex-end;
    transform: translateZ(0);
`

const Card = styled.div`
    position: relative;
    width: 650px;
    overflow: hidden;
    height: 375px;
    background-color: ${theme.colors.paperGrey};
    border: 3px solid ${theme.colors.accentBlue};
    box-shadow: 4px 4px 0px ${theme.colors.accentBlue};
    border-radius: ${theme.borderRadius.normal};
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    img {
        background-repeat: no-repeat;
        object-fit: cover;
    }
    .text {
        padding: 35px 25px 10px 25px;
        display: flex;
        flex-direction: column;
        gap: 5px;
        background-color: ${theme.colors.white};
    }
    @media (max-width: 750px) {
        width: 100%;
        height: 550px;
        grid-template-columns: 1fr;
        img {
            height: 250px;
            width: 100%;
            border-radius: ${theme.borderRadius.large} ${theme.borderRadius.large} 0 0;
            order: 0;
        }
        .text {
            min-height: 300px;
            height: auto;
            order: 1;
            padding: 15px;
            h4,
            h5,
            p {
                text-align: center;
            }
        }
    }
`

const CarouselDot = styled.div<CarouselDotProps>`
    height: 16px;
    width: 16px;
    background-color: ${({ isClicked }) => (isClicked ? theme.colors.accentBlue : theme.colors.white)};
    border: 2px solid ${theme.colors.accentBlue};
    border-radius: 15px;
    z-index: 9;
    margin: 0 10px;
    cursor: pointer;
    z-index: 1;
    @media (max-width: 750px) {
        margin: 25px 15px;
        height: 12px;
        width: 12px;
    }
`

export default function TestimonialCarousel({ carouselData, buttonLink, buttonText }: CarouselProps) {
    const [count, setCount] = useState(0)
    const [direction, toggleDirection] = useToggleState(false)
    const md = useMedia(deviceSize.md)
    const transitions = useTransition([count], (item) => item, {
        from: {
            transform: `translate3d(${direction ? '' : '-'}${md ? 0 : '25%'},0,0)`,
            opacity: 0,
            position: 'absolute',
        },
        enter: { transform: 'translate3d(0,0,0)', opacity: 1 },
        leave: {
            opacity: 0,
            transform: `translate3d(${direction ? '-' : ''}${md ? 0 : '25%'},0,0)`,
        },
    })

    const prevSlide = () => {
        if (direction) toggleDirection()
        const prevSlide = count - 1 < 0 ? carouselData.length - 1 : count - 1
        setCount(prevSlide)
    }

    const nextSlide = () => {
        if (!direction) toggleDirection()
        const nextSlide = count + 1 < carouselData.length ? count + 1 : 0
        setCount(nextSlide)
    }

    const toggleDot = (index: number) => {
        if (index > count) {
            if (!direction) toggleDirection()
        } else {
            if (direction) toggleDirection()
        }
        setCount(index)
    }

    return (
        <Component>
            <Container>
                {!md ? (
                    <CarouselIconButton onClick={prevSlide} isLeft>
                        <div className="circle">
                            <ArrowBackIcon />
                        </div>
                    </CarouselIconButton>
                ) : null}
                <Wrapper>
                    {transitions.map(({ item, props, key }) => {
                        const data = carouselData[item]
                        return (
                            <CarouselCard key={key} style={props}>
                                <Card>
                                    <div className="text">
                                        <Headline4 color={theme.colors.subtitleBlack}>{data.footerInfo?.title}</Headline4>
                                        <Subtitle1 color={theme.colors.body2Black} marginBottom={20}>
                                            {data.footerInfo?.subtitle}
                                        </Subtitle1>
                                        <QuoteText color={theme.colors.subtitleBlack}>{data.text}</QuoteText>
                                    </div>
                                    <NextImage
                                        src={data.image}
                                        alt="Testimonial user"
                                        width={275}
                                        height={375}
                                        objectPosition="top"
                                        objectFit="contain"
                                    />
                                </Card>
                            </CarouselCard>
                        )
                    })}
                </Wrapper>
                {!md ? (
                    <CarouselIconButton onClick={nextSlide}>
                        <div className="circle">
                            <ArrowForwardIcon />
                        </div>
                    </CarouselIconButton>
                ) : null}
            </Container>
            <DotsWrapper>
                {md ? (
                    <CarouselIconButton onClick={prevSlide}>
                        <div className="circle">
                            <ArrowBackIcon />
                        </div>
                    </CarouselIconButton>
                ) : null}
                {carouselData.map((_, i) => (
                    <CarouselDot key={`carousel-dot-${i}`} isClicked={count === i} onClick={() => toggleDot(i)} />
                ))}
                {md ? (
                    <CarouselIconButton onClick={nextSlide}>
                        <div className="circle">
                            <ArrowForwardIcon />
                        </div>
                    </CarouselIconButton>
                ) : null}
            </DotsWrapper>
            {buttonLink && buttonText ? (
                <DynamicButton
                    href={buttonLink}
                    width={280}
                    height={50}
                    borderRadius={theme.borderRadius.normal}
                    color={theme.colors.accentBlue}
                >
                    <ButtonText color={theme.colors.white}>{buttonText}</ButtonText>
                </DynamicButton>
            ) : null}
        </Component>
    )
}
